/* eslint-disable @typescript-eslint/no-explicit-any */
import { api } from "api/root";
import axios, { CancelTokenSource } from "axios";
import { handleRequestErrors } from "helpers/functions/commons";
import { Dispatch } from "react";
import { addNotification } from "store/notifications/slice";
import { IdentifierFieldUnit, IProductionConfirmResult, IProductionDeleteResult, IProductionStage, IUserProductionRowResult, IUserProductionSeries, IUserProductionSummary } from "./types";

export interface IRequestError extends Error {
    status?: number;
}

// The production calls are made from components, which have no thunkAPI to reject through. Passing
// null used to throw a TypeError on `.dispatch`, destroying the real error before it could be
// shown — so with no thunkAPI the message is thrown for the caller to surface instead.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const failRequest = (e: unknown, thunkAPI: unknown): any => {
    const validationMessage = handleRequestErrors(e)
    if (!thunkAPI) {
        const error: IRequestError = new Error(validationMessage || 'Something went wrong. Please try again.');
        error.status = (e as { response?: { status?: number } })?.response?.status;
        throw error;
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const thunkRejectError = thunkAPI as { rejectWithValue: (message: any) => any, dispatch: Dispatch<unknown> }
    thunkRejectError.dispatch(addNotification({ type: 'error', message: validationMessage }));
    return thunkRejectError.rejectWithValue(validationMessage);
}

// responseType 'blob' applies to the error response too, so the API's own message arrives as a Blob
// that handleRequestErrors cannot read, and every failure reports the generic fallback instead.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const readBlobErrorBody = async (e: any): Promise<unknown> => {
    const data = e?.response?.data;
    if (!(data instanceof Blob)) return e;

    const text = await data.text();
    try {
        e.response.data = JSON.parse(text);
    } catch {
        e.response.data = text;
    }
    return e;
}

export const R_addNewDataset = async (data: FormData, thunkAPI: unknown) => {
    try {
        const res = await axios.post(`${process.env.REACT_APP_API_URL}Dataset`, data, {
            withCredentials: true, 
            headers: {
                "Content-Type": "multipart/form-data",
            },
        })
        const downloadUrl = window.URL.createObjectURL(new Blob([res.data]));
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.setAttribute('download', 'file.xlsx'); // any other extension
        document.body.appendChild(link);
        link.click();
        link.remove();
        return res;
    } catch (e: unknown) {
        const validationMessage = handleRequestErrors(e)
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const thunkRejectError = thunkAPI as { rejectWithValue: (message: any) => any, dispatch: Dispatch<unknown> }
        thunkRejectError.dispatch(addNotification({ type: 'error', message: validationMessage }));
        return thunkRejectError.rejectWithValue(validationMessage);
    }
}

export const R_addEmptyDataset = async (data: FormData, thunkAPI: unknown) => {
    try {
        const result = await axios.post(`${process.env.REACT_APP_API_URL}Dataset/Empty`, data, {
            withCredentials: true, 
            headers: {
                "Content-Type": "multipart/form-data",
            },
        })
        return result;
    } catch (e: unknown) {
        const validationMessage = handleRequestErrors(e)
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const thunkRejectError = thunkAPI as { rejectWithValue: (message: any) => any, dispatch: Dispatch<unknown> }
        thunkRejectError.dispatch(addNotification({ type: 'error', message: validationMessage }));
        return thunkRejectError.rejectWithValue(validationMessage);
    }
}

export const R_importDataset = async (data: {id:string, file:FormData}, thunkAPI: unknown) => {
    try {
        const res = await axios.post(`${process.env.REACT_APP_API_URL}Dataset/${data.id}/import`, data.file, {
            withCredentials: true, 
            responseType: 'blob',
            headers: {
                "Content-Type": "multipart/form-data",
            },
        })
        
        const downloadUrl = window.URL.createObjectURL(new Blob([res.data]));
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.setAttribute('download', 'file.xlsx'); // any other extension
        document.body.appendChild(link);
        link.click();
        link.remove();
    } catch (e: unknown) {
        const validationMessage = handleRequestErrors(e)
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const thunkRejectError = thunkAPI as { rejectWithValue: (message: any) => any, dispatch: Dispatch<unknown> }
        thunkRejectError.dispatch(addNotification({ type: 'error', message: validationMessage }));
        return thunkRejectError.rejectWithValue(validationMessage);
    }
}


export const R_getDatasetsList = async (thunkAPI: unknown) => {
    try {
        const res = await api.get('Dataset/GetAll')
        return res.data
    } catch (e: unknown) {
        const validationMessage = handleRequestErrors(e)
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const thunkRejectError = thunkAPI as { rejectWithValue: (message: any) => any, dispatch: Dispatch<unknown> }
        thunkRejectError.dispatch(addNotification({ type: 'error', message: validationMessage }));
        return thunkRejectError.rejectWithValue(validationMessage);
    }
}

export const R_getDatasetsListByWorkspace = async (data: {workspaceId: string, identifierFieldType: number},thunkAPI: unknown) => {
    try {
        const res = await api.get(`/Workspace/${data.workspaceId}/Datasets/${data.identifierFieldType}`)
        return res.data
    } catch (e: unknown) {
        const validationMessage = handleRequestErrors(e)
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const thunkRejectError = thunkAPI as { rejectWithValue: (message: any) => any, dispatch: Dispatch<unknown> }
        thunkRejectError.dispatch(addNotification({ type: 'error', message: validationMessage }));
        return thunkRejectError.rejectWithValue(validationMessage);
    }
}

export const R_getDataset = async (data: any, thunkAPI: unknown) => {
    try {
        const res = await api.post('Data/AddDataset', data)

        return res.data
    } catch (e: unknown) {
        const validationMessage = handleRequestErrors(e)
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const thunkRejectError = thunkAPI as { rejectWithValue: (message: any) => any, dispatch: Dispatch<unknown> }
        thunkRejectError.dispatch(addNotification({ type: 'error', message: validationMessage }));
        return thunkRejectError.rejectWithValue(validationMessage);
    }
}

export const R_editDatasetValues = async (data: {id: string, title: string, description: string, colorDisplayOption: number}, thunkAPI: unknown) => {
    try {
        const res = await api.put(`Dataset/${data.id}`, { title: data.title, description: data.description, colorDisplayOption: data.colorDisplayOption })
        return res.data
    } catch (e: unknown) {
        const validationMessage = handleRequestErrors(e)
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const thunkRejectError = thunkAPI as { rejectWithValue: (message: any) => any, dispatch: Dispatch<unknown> }
        thunkRejectError.dispatch(addNotification({ type: 'error', message: validationMessage }));
        return thunkRejectError.rejectWithValue(validationMessage);
    }
}


export const R_deleteDataset = async (data: {id: string}, thunkAPI: unknown) => {
    try {
        const res = await api.delete(`Dataset/${data.id}`)
        return res.data
    } catch (e: unknown) {
        const validationMessage = handleRequestErrors(e)
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const thunkRejectError = thunkAPI as { rejectWithValue: (message: any) => any, dispatch: Dispatch<unknown> }
        thunkRejectError.dispatch(addNotification({ type: 'error', message: validationMessage }));
        return thunkRejectError.rejectWithValue(validationMessage);
    }
}

export const R_unshareDataset = async (data: {id: string}, thunkAPI: unknown) => {
    try {
        const res = await api.delete(`Dataset/${data.id}/Unshare`)
        return res.data
    } catch (e: unknown) {
        const validationMessage = handleRequestErrors(e)
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const thunkRejectError = thunkAPI as { rejectWithValue: (message: any) => any, dispatch: Dispatch<unknown> }
        thunkRejectError.dispatch(addNotification({ type: 'error', message: validationMessage }));
        return thunkRejectError.rejectWithValue(validationMessage);
    }
}


export const R_getDatasetData = async (data: {id: string, offset: number, limit: number}, thunkAPI: unknown) => {
    try {
        const res = await api.get(`Dataset/${data.id}/Data`, {params: {offset: data.offset, limit: data.limit}})
        return res.data
    } catch (e: unknown) {
        const validationMessage = handleRequestErrors(e)
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const thunkRejectError = thunkAPI as { rejectWithValue: (message: any) => any, dispatch: Dispatch<unknown> }
        thunkRejectError.dispatch(addNotification({ type: 'error', message: validationMessage }));
        return thunkRejectError.rejectWithValue(validationMessage);
    }
}

export const R_getDatasetProductionSummary = async (data: {id: string}, thunkAPI: unknown): Promise<IUserProductionSummary> => {
    try {
        const res = await api.get(`Dataset/${data.id}/Production`)
        return res.data
    } catch (e: unknown) {
        return failRequest(e, thunkAPI);
    }
}

export const R_getFieldProductionSeries = async (data: {id: string, identifierFieldId: string}, thunkAPI: unknown): Promise<IUserProductionSeries> => {
    try {
        const res = await api.get(`Dataset/${data.id}/Production/${data.identifierFieldId}`)
        return res.data
    } catch (e: unknown) {
        return failRequest(e, thunkAPI);
    }
}

// column is the template header, which is also the tag attribute key the Production View renders from.
// value null clears the cell; 0 is a reading like any other and stores as 0.
export const R_editProductionCell = async (data: {id: string, identifierFieldId: string, date: string, column: string, value: number | null}, thunkAPI: unknown): Promise<IUserProductionRowResult> => {
    try {
        const res = await api.patch(`Dataset/${data.id}/Production/${data.identifierFieldId}`, {
            date: data.date,
            column: data.column,
            value: data.value,
        })
        return res.data
    } catch (e: unknown) {
        return failRequest(e, thunkAPI);
    }
}

export const R_deleteFieldProductionData = async (data: {id: string, identifierFieldId: string}, thunkAPI: unknown): Promise<IProductionDeleteResult> => {
    try {
        const res = await api.delete(`Dataset/${data.id}/Production/${data.identifierFieldId}`)
        return res.data
    } catch (e: unknown) {
        return failRequest(e, thunkAPI);
    }
}

export const R_deleteDatasetRow = async (data: {id: string, identifierFieldId: string}, thunkAPI: unknown) => {
    try {
        const res = await api.delete(`/Dataset/${data.id}/IdentifierField/${data.identifierFieldId}`)
        return res.data
    } catch (e: unknown) {
        const validationMessage = handleRequestErrors(e)
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const thunkRejectError = thunkAPI as { rejectWithValue: (message: any) => any, dispatch: Dispatch<unknown> }
        thunkRejectError.dispatch(addNotification({ type: 'error', message: validationMessage }));
        return thunkRejectError.rejectWithValue(validationMessage);
    }
}

export const R_editDatasetRow = async (data: {id: string, identifierFieldId: string, tags: { tagAttributeId: string, value: string}[]}, thunkAPI: unknown) => {
    try {
        const res = await api.patch(`/Dataset/${data.id}/IdentifierField/${data.identifierFieldId}`, {Tags: data.tags})
        return res.data
    } catch (e: unknown) {
        const validationMessage = handleRequestErrors(e)
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const thunkRejectError = thunkAPI as { rejectWithValue: (message: any) => any, dispatch: Dispatch<unknown> }
        thunkRejectError.dispatch(addNotification({ type: 'error', message: validationMessage }));
        return thunkRejectError.rejectWithValue(validationMessage);
    }
}

export const R_addEmptyDatasetRow = async (data: {id: string}, thunkAPI: unknown) => {
    try {
        const res = await api.post(`/Dataset/${data.id}/EmptyIdentifierField`)
        return res.data
    } catch (e: unknown) {
        const validationMessage = handleRequestErrors(e)
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const thunkRejectError = thunkAPI as { rejectWithValue: (message: any) => any, dispatch: Dispatch<unknown> }
        thunkRejectError.dispatch(addNotification({ type: 'error', message: validationMessage }));
        return thunkRejectError.rejectWithValue(validationMessage);
    }
}

export const R_shareDataset = async (data: {id: string, sharedWith: string[], source?: CancelTokenSource }, thunkAPI: unknown) => {
    try {
        const config = data.source?.token ? {cancelToken: data.source.token} : {}
        const res = await api.put(`/Dataset/${data.id}/Share`, {sharedWith: data.sharedWith}, config)
        return res.data
    } catch (e: unknown) {
        const validationMessage = handleRequestErrors(e)
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const thunkRejectError = thunkAPI as { rejectWithValue: (message: any) => any, dispatch: Dispatch<unknown> }
        thunkRejectError.dispatch(addNotification({ type: 'error', message: validationMessage }));
        return thunkRejectError.rejectWithValue(validationMessage);
    }
}

export const R_exportDataset = async (data: {id: string }, thunkAPI: unknown) => {
    try {
        const res = await axios.post(`${process.env.REACT_APP_API_URL}Dataset/${data.id}/Export`, {}, {
            withCredentials: true, 
            responseType: 'blob',
            headers: {
                "Content-Type": "multipart/form-data",
            },
        })
        
        const downloadUrl = window.URL.createObjectURL(new Blob([res.data]));
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.setAttribute('download', 'file.xlsx'); // any other extension
        document.body.appendChild(link);
        link.click();
        link.remove();


        return res.data
    } catch (e: unknown) {
        const validationMessage = handleRequestErrors(e)
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const thunkRejectError = thunkAPI as { rejectWithValue: (message: any) => any, dispatch: Dispatch<unknown> }
        thunkRejectError.dispatch(addNotification({ type: 'error', message: validationMessage }));
        return thunkRejectError.rejectWithValue(validationMessage);
    }
}

// Blocking problems arrive on a 200 in canConfirm/blockedBy. An error here means the file could
// not be processed at all — malformed, or over the size cap.
export const R_stageProductionData = async (data: {id: string, file: File, unit: IdentifierFieldUnit }, thunkAPI: unknown): Promise<IProductionStage> => {
    try {
        const formData = new FormData();
        formData.append('file', data.file);

        const res = await axios.post(`${process.env.REACT_APP_API_URL}Dataset/${data.id}/Production/Stage`, formData, {
            params: {unit: String(data.unit)},
            withCredentials: true,
            headers: {
                "Content-Type": "multipart/form-data",
            },
        })
        return res.data
    } catch (e: unknown) {
        return failRequest(e, thunkAPI);
    }
}

export const R_confirmProductionStage = async (data: {id: string, stageId: string, createFields?: string[] }, thunkAPI: unknown): Promise<IProductionConfirmResult> => {
    try {
        const res = await api.post(`Dataset/${data.id}/Production/Stage/${data.stageId}`, { createFields: data.createFields ?? [] })
        return res.data
    } catch (e: unknown) {
        return failRequest(e, thunkAPI);
    }
}

// Best-effort: an abandoned stage expires on its own, and re-staging supersedes it.
export const R_cancelProductionStage = async (data: {id: string, stageId: string }) => {
    try {
        await api.delete(`Dataset/${data.id}/Production/Stage/${data.stageId}`)
    } catch (e: unknown) {
        return;
    }
}

export const R_exportProductionTemplate = async (data: {unit: IdentifierFieldUnit }, thunkAPI: unknown) => {
    try {
        const res = await axios.get(`${process.env.REACT_APP_API_URL}Dataset/Export/Template/Production`, {
            withCredentials: true,
            responseType: 'blob',
            params: { unit: data.unit },
        })

        const downloadUrl = window.URL.createObjectURL(new Blob([res.data]));
        const link = document.createElement('a');
        const units = data.unit === IdentifierFieldUnit.Metric ? 'Metric Units' : 'Field Units';

        link.href = downloadUrl;
        // Mirrors the Content-Disposition name the API sets; that header is not readable here
        // unless the API adds it to Access-Control-Expose-Headers.
        link.setAttribute("download", `bMark Production Data Template - ${units}.xlsx`);
        document.body.appendChild(link);
        link.click();
        link.remove();

        return res.data
    } catch (e: unknown) {
        return failRequest(e, thunkAPI);
    }
}

export const R_exportProductionData = async (data: {id: string, identifierFieldIds: string[], fileName: string }, thunkAPI: unknown) => {
    try {
        const res = await axios.get(`${process.env.REACT_APP_API_URL}Dataset/${data.id}/Production/Export`, {
            withCredentials: true,
            responseType: 'blob',
            params: { identifierFieldIds: data.identifierFieldIds },
            // Axios brackets repeated params (ids[]=a&ids[]=b) by default, which model binding does
            // not read; indexes: null gives the plain ids=a&ids=b form it does.
            paramsSerializer: { indexes: null },
        })

        const downloadUrl = window.URL.createObjectURL(new Blob([res.data]));
        const link = document.createElement('a');

        link.href = downloadUrl;
        link.setAttribute("download", `${data.fileName}.xlsx`);
        document.body.appendChild(link);
        link.click();
        link.remove();

        return res.data
    } catch (e: unknown) {
        return failRequest(await readBlobErrorBody(e), thunkAPI);
    }
}

export const R_exportDatasetTemplate = async (data: {datasetType: string, fileName: string }, thunkAPI: unknown) => {
    try {
        const res = await axios.get(`${process.env.REACT_APP_API_URL}Dataset/Export/Template/${data.datasetType}`, {
            withCredentials: true,
            responseType: 'blob',
            headers: {
                "Content-Type": "multipart/form-data",
            },
        })
        
        const downloadUrl = window.URL.createObjectURL(new Blob([res.data]));
        const link = document.createElement('a');
        const filename = `bMark User Data Import Template - ${data.fileName}.xlsx`

        link.href = downloadUrl;
        link.setAttribute("download", filename);
        document.body.appendChild(link);
        link.click();
        link.remove();

        return res.data
    } catch (e: unknown) {
        const validationMessage = handleRequestErrors(e)
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const thunkRejectError = thunkAPI as { rejectWithValue: (message: any) => any, dispatch: Dispatch<unknown> }
        thunkRejectError.dispatch(addNotification({ type: 'error', message: validationMessage }));
    }
}

export const R_changeDatasetViewOrder = async (data: {datasetId: string, gridColumns: {tagAttributeId: string, isFreezed?: boolean, order?: number, sortingType?: number, sortingPriority?: number}[]}, thunkAPI: unknown) => {
    try {
        const response = await api.put(`/Dataset/${data.datasetId}/Columns`, { gridColumns: data.gridColumns });
        return {...response.data};
    } catch (e: unknown) {
        const validationMessage = handleRequestErrors(e)
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const thunkRejectError = thunkAPI as { rejectWithValue: (message: any) => any, dispatch: Dispatch<unknown> }
        thunkRejectError?.dispatch(addNotification({ type: 'error', message: validationMessage }));
        return thunkRejectError?.rejectWithValue(validationMessage) || {errorMsg: validationMessage};
    }
}